import { useState, useEffect } from "react";
import { Box, Container, Button } from "@mui/material";
import theme from "../../theme";

export const SectionNavBar = ({
  sections,
  activeSection,
  onSectionClick,
  scrollThreshold = 200,
  topPosition = 0,
}) => {
  const [showSectionBar, setShowSectionBar] = useState(false);

  // When the Section bar should show
  useEffect(() => {
    const handleScroll = () => {
      setShowSectionBar(window.scrollY > scrollThreshold);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    // Clear the scroll when component unmounts
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrollThreshold]);

  const handleSectionClick = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Calculate offset to account for fixed navbar height
      const navbarHeight = 60;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
    // Call the parent's onSectionClick
    if (onSectionClick) {
      onSectionClick(sectionId);
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: `${topPosition}px`,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar - 1,
        visibility: showSectionBar ? "visible" : "hidden",
        pointerEvents: showSectionBar ? "auto" : "none",
        opacity: showSectionBar ? 1 : 0,
        transition: "opacity 0.3s ease-in-out, visibility 0.3s ease-in-out",
        backgroundColor: theme.customBackgrounds.darkBackground,
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "flex-start", md: "space-evenly" },
            gap: { xs: 0.5, md: 1 },
            py: { xs: 1.2, md: 1.5 },
            px: { xs: 1, md: 0 },
            overflowX: { xs: "auto", md: "visible" },
            overflowY: "hidden",
            whiteSpace: "nowrap",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {sections.map((section) => {
            const isActive = activeSection === section.id;
            return (
              <Button
                key={section.id}
                onClick={() => handleSectionClick(section.id)}
                sx={{
                  flexShrink: 0,
                  px: { xs: 2, md: 2.5 },
                  py: { xs: 0.4, md: 0.5 },
                  borderRadius: "6px",
                  position: "relative",
                  overflow: "hidden",
                  color: isActive ? "primary.brandSecondary" : "primary.main",
                  backgroundColor: isActive
                    ? `${theme.palette.primary.main}1A` // 10% opacity
                    : "transparent",
                  fontWeight: 600,
                  fontSize: "body2",
                  textTransform: "none",
                  border: `1px solid transparent`,
                  width: "auto",
                  minWidth: "auto",
                  "&:hover": {
                    color: "primary.brandSecondary",
                    borderColor: "primary.brandSecondary",
                    transition: "none",
                  },
                }}
              >
                {section.label}
              </Button>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};
